import { FileOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { TOUR_DEMO_ACTIVITY, TOUR_DEMO_FILES, TOUR_DEMO_PROJECT } from "../content/tour-demo-data.ts"
import { DemoProjectFinanceSection } from "./demo-sections/DemoProjectFinanceSection.tsx"
import { DemoProjectInvoicesSection } from "./demo-sections/DemoProjectInvoicesSection.tsx"
import { DemoProjectMilestonesSection } from "./demo-sections/DemoProjectMilestonesSection.tsx"

const { Title, Text } = Typography

export type ProjectPreviewSection =
  | "overview"
  | "milestones"
  | "finance"
  | "invoices"
  | "files"
  | "activity"

const SECTIONS: { key: ProjectPreviewSection; labelKey: string; route: string }[] = [
  {
    key: "overview",
    labelKey: "guide.productTour.context.projectOverview",
    route: RoutesPaths.guide.tourPreview.project
  },
  {
    key: "milestones",
    labelKey: "guide.productTour.context.projectMilestones",
    route: RoutesPaths.guide.tourPreview.projectMilestones
  },
  {
    key: "finance",
    labelKey: "guide.productTour.context.projectFinance",
    route: RoutesPaths.guide.tourPreview.projectFinance
  },
  {
    key: "invoices",
    labelKey: "guide.productTour.context.projectInvoices",
    route: RoutesPaths.guide.tourPreview.projectInvoices
  },
  {
    key: "files",
    labelKey: "guide.productTour.context.projectFiles",
    route: RoutesPaths.guide.tourPreview.projectFiles
  },
  {
    key: "activity",
    labelKey: "guide.productTour.context.projectActivity",
    route: RoutesPaths.guide.tourPreview.projectActivity
  }
]

const FilesSection = () => {
  const { t } = useTranslation()
  return (
    <div data-guide-target="project-files-panel">
      <CardComponent className="rounded-lg" title={t("guide.tourDemo.project.files.title")}>
        <Flex vertical gap={8}>
          {TOUR_DEMO_FILES.map((file) => (
            <Flex key={file} align="center" gap={8}>
              <FileOutlined className="text-gray-400" />
              <Text className="text-sm">{file}</Text>
            </Flex>
          ))}
        </Flex>
      </CardComponent>
    </div>
  )
}

const ActivitySection = () => {
  const { t } = useTranslation()
  return (
    <div data-guide-target="project-activity-panel">
      <CardComponent className="rounded-lg" title={t("guide.tourDemo.project.activity.title")}>
        <Flex vertical gap={10}>
          {TOUR_DEMO_ACTIVITY.map((entry) => (
            <Flex key={entry} align="center" gap={10}>
              <span className="bg-primary block size-1.5 shrink-0 rounded-full" />
              <Text className="text-sm">{entry}</Text>
            </Flex>
          ))}
        </Flex>
      </CardComponent>
    </div>
  )
}

/**
 * A static, hardcoded "Miller Kitchen Remodel" Project — the Product Tour's
 * counterpart to `GuideTourPreviewLeadPage`, routed to instead of a real
 * `/projects/:id/*` whenever the account has no real Project yet. Each
 * section is its own route (mirroring the real Project workspace's own
 * sub-routes) so the tour's step-by-step SHOW→NAVIGATE→WAIT→HIGHLIGHT
 * engine needs no changes at all — only `product-tour-steps.ts`'s
 * `resolveRoute` fallbacks point here instead of returning `null`.
 */
export const GuideTourPreviewProjectPage = ({
  section
}: {
  section: ProjectPreviewSection
}) => {
  const { t } = useTranslation()
  const p = TOUR_DEMO_PROJECT
  const customerName = p.temporary_customer
    ? `${p.temporary_customer.first_name} ${p.temporary_customer.last_name}`
    : ""
  const projectManagerName = p.project_managers?.[0]
    ? `${p.project_managers[0].first_name} ${p.project_managers[0].last_name}`
    : ""
  const address = [p.address, p.city, p.state].filter(Boolean).join(", ")
  // The real Milestones/Finance/Invoices tables are wide (12+ columns) — a
  // narrow centered column here would squash them into something that no
  // longer resembles the real screen, defeating the point of reusing the
  // real component. Only the intro/files/activity sections keep the
  // narrower reading width.
  const isWideSection = section === "milestones" || section === "finance" || section === "invoices"

  useEffect(() => {
    generateDocumentTitle(p.lead_name)
  }, [p.lead_name])

  return (
    <Flex vertical gap={16} className={isWideSection ? "pb-10" : "mx-auto max-w-3xl pb-10"}>
      <Flex align="center" gap={8}>
        <Tag color="gold" icon={<InfoCircleOutlined />}>
          {t("guide.tourDemo.exampleProjectBadge")}
        </Tag>
        <Text type="secondary" className="text-xs">
          {t("guide.tourDemo.exampleNotice")}
        </Text>
      </Flex>

      <div data-guide-target="project-header-panel">
        <CardComponent className="rounded-lg">
          <Flex vertical gap={12}>
            <Flex align="center" justify="space-between">
              <Title level={5} className="!mb-0">
                {p.lead_name}
              </Title>
              <Tag color="processing">{p.project_status_name}</Tag>
            </Flex>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.customer")}
                </Text>
                <Text className="text-sm font-medium">{customerName}</Text>
              </Flex>
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.contractValue")}
                </Text>
                <Text className="text-sm font-medium">{budgetFormat(Number(p.sales_price))}</Text>
              </Flex>
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.start")}
                </Text>
                <Text className="text-sm font-medium">{p.start_date}</Text>
              </Flex>
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.targetCompletion")}
                </Text>
                <Text className="text-sm font-medium">{p.end_date}</Text>
              </Flex>
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.projectManager")}
                </Text>
                <Text className="text-sm font-medium">{projectManagerName}</Text>
              </Flex>
              <Flex vertical gap={0}>
                <Text type="secondary" className="text-xs">
                  {t("guide.tourDemo.project.address")}
                </Text>
                <Text className="text-sm font-medium">{address}</Text>
              </Flex>
            </div>
          </Flex>
        </CardComponent>
      </div>

      <Flex gap={16} wrap>
        {SECTIONS.map((s) => (
          <Link
            key={s.key}
            to={s.route}
            className={
              s.key === section ? "text-sm font-semibold text-primary" : "text-sm text-gray-500"
            }
          >
            {t(s.labelKey)}
          </Link>
        ))}
      </Flex>

      {section === "milestones" && <DemoProjectMilestonesSection />}
      {section === "finance" && <DemoProjectFinanceSection />}
      {section === "invoices" && <DemoProjectInvoicesSection />}
      {section === "files" && <FilesSection />}
      {section === "activity" && <ActivitySection />}
    </Flex>
  )
}
